import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import useAxios from "../../../../hooks/useAxios";
import { IGetStudents2, IStudent2 } from "../../../../models/shuttleSetting.model";
import { RootState } from "../../../../store/rootReducer";
import ErrorResponse from "../../../ErrorResponse";
import Loader from "../../../Loader";
import { Button as AntButton, Modal, Select, Table, TableColumnsType } from 'antd';
import config from '../../../../lib/config';
import Pagination from '../../../Pagination';
import { createStudents, getStudentList } from "../../../../api/shuttleSetting.api";
import Button from '../../../Button';
import _ from "lodash";
import Checkbox from '../../../Checkbox';
import useNotification from "../../../../hooks/useNotification";
import { IGetCurrentSemester, IGetSemesters } from "../../../../models/semesters.model";
import { getCurrentSemester, getSemesters } from "../../../../api/semesters.api";

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  rideGbn: string;
  runningStationCode: number | undefined;
  onGetStudents: (runningStationCode: number) => void;
}

const SelectStudentModal: FC<IProps> = ({
  visible,
  setVisible,
  rideGbn,
  runningStationCode,
  onGetStudents,
}) => {
  const { t } = useTranslation();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetCurrentSemester>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetSemesters>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetStudents2>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();
  const [data, setData] = useState<IStudent2[]>([]);
  const [page, setPage] = useState(1);
  const [semesterCode, setSemesterCode] = useState<number>();
  const [memberName, setMemberName] = useState('');
  const [memberCodes, setMemberCodes] = useState<string[]>([]);

  useEffect(() => {
    execute(getCurrentSemester());
    execute2(getSemesters());
  }, []);

  useEffect(() => {
    if (res && !isLoading && currentCampus) {
      setSemesterCode(res.data?.semesterCode);
      execute3(getStudentList(currentCampus, res.data?.semesterCode));
    }
  }, [res, isLoading, currentCampus]);

  useEffect(() => {
    if (res3 && !isLoading3) {
      setData(_.cloneDeep(res3.data));
    }
  }, [res3, isLoading3]);

  useEffect(() => {
    if (semesterCode && currentCampus) {
      setMemberName('');
      execute3(getStudentList(currentCampus, semesterCode));
    }
  }, [semesterCode, currentCampus]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      if (runningStationCode) onGetStudents(runningStationCode);
      setMemberCodes([]);
      setVisible(false);
      successNotification(t('common.message.save1'));
    }
  }, [submitRes, submitIsLoading]);

  const onSearch = () => {
    let _data: any = _.cloneDeep(res3?.data);
    _data = _data.filter((item: any) => item.memKorName?.includes(memberName) || item.memEngName?.includes(memberName));

    setData(_data);
  }

  const onSelect = (code: string) => {
    if (memberCodes.includes(code)) {
      setMemberCodes(memberCodes.filter((item: string) => item !== code));
    } else {
      setMemberCodes([...memberCodes, code]);
    }
  }

  const onSubmit = () => {
    if (!runningStationCode) return;

    if (memberCodes.length === 0) {
      return infoNotification(t('student.student.message24'));
    }

    const req: any = [];
    memberCodes.forEach((item: string) => {
      req.push({
        memberCode: item,
        rideGbn,
      });
    });

    submitExecute(createStudents(currentCampus, runningStationCode, req));
  }

  const columns: TableColumnsType<IStudent2> = [
    {
      title: '',
      dataIndex: 'memberCode',
      key: 'memberCode',
      align: 'center',
      render: (value) => (
        <Checkbox
          checked={memberCodes.includes(value)}
          onChange={() => onSelect(value)}
          text=""
        />
      )
    },
    {
      title: t('campus.shuttle.column28'),
      dataIndex: 'memKorName',
      key: 'memKorName',
      align: 'center',
      sorter: (a, b) => a.memKorName?.localeCompare(b.memKorName),
    },
    {
      title: t('campus.staff.column36'),
      dataIndex: 'webId',
      key: 'webId',
      align: 'center',
      sorter: (a, b) => a.webId?.localeCompare(b.webId),
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      key: 'className',
      align: 'center',
      sorter: (a, b) => a.className?.localeCompare(b.className),
    },
  ];

  return (
    <>
      {isLoading || isLoading2 || isLoading3 || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={submitError} />

      <Modal
        title={t('campus.shuttle.title10')}
        visible={visible}
        onCancel={() => {
          setMemberCodes([]);
          setVisible(false);
        }}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onSubmit}
            >
              {t('common.button.select')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={() => {
                setMemberCodes([]);
                setVisible(false);
              }}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="flex">
          <div>
            <Select
              className="w-150p"
              placeholder={t('class.class.placeholder4')}
              value={semesterCode}
              onChange={value => setSemesterCode(value)}
            >
              {res2?.data?.map((item: any) => (
                <Select.Option
                  key={item.semesterCode}
                  value={item.semesterCode}
                >
                  {item.semesterName}
                </Select.Option>
              ))}
            </Select>
          </div>
          <input
            type="text"
            className="form-control input-text ml-1"
            placeholder={t('campus.shuttle.placeholder6')}
            value={memberName}
            onChange={e => setMemberName(e.target.value)}
            onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
          />
          <Button
            className="btn btn-secondary btn-w70p ml-1"
            onClick={onSearch}
            text={t('common.button.search')}
          />
        </div>

        <div>
          <Table
            className='comm-table__area mt-3'
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.miniPageSize,
            }}
          />
        </div>
        <div className="common-paging center">
          <Pagination
            count={data.length || 0}
            page={page}
            setPage={setPage}
            mini
          />
        </div>
      </Modal>
    </>
  );
}

export default SelectStudentModal;
